import { UserRoleEnum } from '@monetize/types/app';
import {
  QuoteTemplateSettingPermission,
  QuoteTemplateSettingsConfig,
  SettingAccessScopeEnum,
  SettingVisibilityEnum,
} from '@monetize/types/experience-config';
import { FieldPath, UseFormSetValue } from 'react-hook-form';
import { FeatureFlag } from '../../../services/launchDarkly';
import {
  QUOTE_TEMPLATE_PERMISSION_ROLES,
  QUOTE_TEMPLATE_SETTINGS_METADATA,
  QuoteTemplateSettingMeta,
} from './quoteTemplateSettings.metadata';

/** Higher rank = more permissive. Used for "most permissive wins" resolution. */
const VISIBILITY_RANK: Record<SettingVisibilityEnum, number> = {
  [SettingVisibilityEnum.HIDDEN]: 0,
  [SettingVisibilityEnum.READ_ONLY]: 1,
  [SettingVisibilityEnum.EDITABLE]: 2,
};

/**
 * Resolves the effective visibility of a single quote-template setting for a
 * user, given the role-based config and the user's roles.
 *
 * Rules (mirrors the Custom Field visibility feature, BP-15571):
 * - Setting not configured at all → EDITABLE (open baseline / backwards compatible).
 * - ALL_ROLES → the single `allRolesVisibility`.
 * - SPECIFIC_ROLES → most permissive (EDITABLE > READ_ONLY > HIDDEN) across the
 *   user's roles. If none of the user's roles are configured → HIDDEN (default-closed).
 */
export function resolveSettingVisibility(
  config: QuoteTemplateSettingsConfig | undefined,
  settingKey: string,
  userRoleNames: UserRoleEnum[],
): SettingVisibilityEnum {
  const permission = config?.settings?.[settingKey];

  if (!permission) {
    return SettingVisibilityEnum.EDITABLE;
  }

  if (permission.accessScope === SettingAccessScopeEnum.ALL_ROLES) {
    return permission.allRolesVisibility;
  }

  const visibilities = userRoleNames
    .map((role) => permission.roleVisibility?.[role])
    .filter((visibility): visibility is SettingVisibilityEnum => !!visibility);

  if (visibilities.length === 0) {
    return SettingVisibilityEnum.HIDDEN;
  }

  return visibilities.reduce((mostPermissive, current) =>
    VISIBILITY_RANK[current] > VISIBILITY_RANK[mostPermissive]
      ? current
      : mostPermissive,
  );
}

const ACCESS_SUMMARY_BY_VISIBILITY: Record<SettingVisibilityEnum, string> = {
  [SettingVisibilityEnum.EDITABLE]: 'Editable by all roles',
  [SettingVisibilityEnum.READ_ONLY]: 'Read-only for all roles',
  [SettingVisibilityEnum.HIDDEN]: 'Hidden for all roles',
};

export const ACCESS_SUMMARY_VARIES = 'Varies by role';

/**
 * One-line summary of how a setting's access is configured, shown in the
 * setting-card header so the config is scannable without expanding the card.
 * Specific-role configs where every role resolves to the same visibility read
 * the same as the equivalent all-roles config.
 */
export function getSettingAccessSummary(
  permission: QuoteTemplateSettingPermission | undefined,
): string {
  if (!permission) {
    return ACCESS_SUMMARY_BY_VISIBILITY[SettingVisibilityEnum.EDITABLE];
  }

  if (permission.accessScope === SettingAccessScopeEnum.ALL_ROLES) {
    return ACCESS_SUMMARY_BY_VISIBILITY[permission.allRolesVisibility];
  }

  const visibilities = new Set(
    QUOTE_TEMPLATE_PERMISSION_ROLES.map(
      (role) =>
        permission.roleVisibility?.[role] ?? SettingVisibilityEnum.EDITABLE,
    ),
  );

  if (visibilities.size === 1) {
    const [onlyVisibility] = visibilities;
    return ACCESS_SUMMARY_BY_VISIBILITY[onlyVisibility];
  }

  return ACCESS_SUMMARY_VARIES;
}

export interface QuoteTemplateSectionGates {
  /** Amendment-only settings are only rendered for amendment (V2) quotes. */
  isAmendmentV2Quote?: boolean;
  /** Billing-schedule settings are hidden when the tenant disables the schedule. */
  hideBillingSchedule?: boolean;
  /** Feature flags, for settings gated behind `requiresFeatureFlag`. */
  flags?: Partial<FeatureFlag>;
}

/**
 * Whether a setting's feature flag (if it declares one) is on. Shared so the
 * settings list, the section headers and the per-quote form all hide a flagged
 * setting together.
 */
export function isSettingFlagEnabled(
  { requiresFeatureFlag }: QuoteTemplateSettingMeta,
  flags: Partial<FeatureFlag> | undefined,
): boolean {
  return !requiresFeatureFlag || !!flags?.[requiresFeatureFlag];
}

/**
 * Returns the sections of the quote customization form that have at least one
 * setting the user can see — i.e. not structurally excluded (amendment-only /
 * billing-schedule gates) and not resolved to HIDDEN for the user's roles.
 *
 * An empty result means the user can't customize anything: callers hide the
 * whole customize entry point in that case. Section headers render only for
 * sections present in the result.
 */
export function getVisibleQuoteTemplateSections(
  visibilityFor: (fieldName: string) => SettingVisibilityEnum,
  { isAmendmentV2Quote, hideBillingSchedule, flags }: QuoteTemplateSectionGates,
): Set<QuoteTemplateSettingMeta['section']> {
  const visibleSections = new Set<QuoteTemplateSettingMeta['section']>();

  QUOTE_TEMPLATE_SETTINGS_METADATA.forEach((meta) => {
    if (meta.isAmendmentOnly && !isAmendmentV2Quote) {
      return;
    }
    if (meta.requiresBillingScheduleEnabled && hideBillingSchedule) {
      return;
    }
    if (!isSettingFlagEnabled(meta, flags)) {
      return;
    }
    if (visibilityFor(meta.key) === SettingVisibilityEnum.HIDDEN) {
      return;
    }
    visibleSections.add(meta.section);
  });

  return visibleSections;
}

/**
 * Recursively checks an entry of RHF's `dirtyFields` marker tree (nested
 * objects with `true` leaves) for any dirty field. Used to flag a setting card
 * as unsaved when anything under its permission entry changed.
 */
export function hasAnyDirtyField(dirtyMarker: unknown): boolean {
  if (dirtyMarker === true) {
    return true;
  }
  if (!dirtyMarker || typeof dirtyMarker !== 'object') {
    return false;
  }
  return Object.values(dirtyMarker).some(hasAnyDirtyField);
}

/**
 * Sets every role's visibility for a setting to the given value ("apply to
 * all"), so admins don't have to change each role dropdown individually.
 */
export function applyVisibilityToAllRoles(
  setValue: UseFormSetValue<QuoteTemplateSettingsConfig>,
  settingKey: string,
  visibility: SettingVisibilityEnum,
): void {
  // RHF widens dynamic record paths, so its value type for these names never
  // narrows to the per-role visibility enum. Re-type setValue with the actual
  // shape of the calls we make instead of casting each value.
  const setRoleVisibility = setValue as unknown as (
    name: FieldPath<QuoteTemplateSettingsConfig>,
    value: SettingVisibilityEnum,
    options?: { shouldDirty: boolean },
  ) => void;

  QUOTE_TEMPLATE_PERMISSION_ROLES.forEach((role) => {
    setRoleVisibility(
      `settings.${settingKey}.roleVisibility.${role}` as FieldPath<QuoteTemplateSettingsConfig>,
      visibility,
      { shouldDirty: true },
    );
  });
}

/**
 * Builds the initial value for the settings-page permissions form. Every
 * controllable setting gets a fully-populated entry (all roles present), merged
 * over any stored config, so that switching a setting to "Specific Roles"
 * always shows an explicit visibility for each role rather than accidentally
 * defaulting unset roles to Hidden.
 */
export function buildQuoteTemplatePermissionsFormValues(
  stored: QuoteTemplateSettingsConfig | undefined,
): QuoteTemplateSettingsConfig {
  const settings: Record<string, QuoteTemplateSettingPermission> = {
    ...stored?.settings,
  };

  QUOTE_TEMPLATE_SETTINGS_METADATA.forEach(({ key }) => {
    const storedSetting = stored?.settings?.[key];

    const roleVisibility = QUOTE_TEMPLATE_PERMISSION_ROLES.reduce<
      Partial<Record<UserRoleEnum, SettingVisibilityEnum>>
    >((acc, role) => {
      acc[role] =
        storedSetting?.roleVisibility?.[role] ?? SettingVisibilityEnum.EDITABLE;
      return acc;
    }, {});

    settings[key] = {
      accessScope:
        storedSetting?.accessScope ?? SettingAccessScopeEnum.ALL_ROLES,
      allRolesVisibility:
        storedSetting?.allRolesVisibility ?? SettingVisibilityEnum.EDITABLE,
      roleVisibility,
    };
  });

  return { settings };
}
